import React, { useEffect, useState } from 'react';
import { Globe, Copy, Check, Share2 } from 'lucide-react';
import { Switch } from './ui/switch';
import { apiClient } from '../api';
import { toast } from 'sonner';

// `/me` card that toggles `public_profile` and surfaces a copyable share URL.
const PublicProfileShare = ({ publicProfile, onChange }) => {
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiClient.get('/profile/me/share-link')
      .then((r) => setShare(r.data))
      .catch((err) => console.debug('Share link not available yet:', err?.message));
  }, [publicProfile]);

  const toggle = async (next) => {
    setBusy(true);
    try {
      // Caller owns the persistence (a full /profile PUT) — we just notify.
      onChange(next);
      toast.success(next ? 'Public preview enabled' : 'Public preview disabled', {
        description: next
          ? 'Anyone with your link can see a sanitized preview.'
          : 'Your share link is no longer accessible.',
      });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy', { description: 'Long-press the link to copy manually.' });
    }
  };

  return (
    <div
      className="bg-fnl-purple/60 border border-purple-900/40 rounded-2xl p-5 mb-6"
      data-testid="public-profile-share"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-fnl-pink/15 flex items-center justify-center shrink-0">
            <Share2 className="w-5 h-5 text-fnl-pink" />
          </div>
          <div>
            <div className="text-white font-bold text-lg">Shareable public preview</div>
            <div className="text-purple-300 text-xs mt-0.5">
              Get a clean link to share on socials. Shows just your name, photo, age, location, bio and interests — no chat, no email.
            </div>
          </div>
        </div>
        <Switch
          checked={publicProfile}
          onCheckedChange={toggle}
          disabled={busy}
          data-testid="public-profile-toggle"
          className="mt-1"
        />
      </div>
      {publicProfile && share?.url && (
        <div className="mt-3 flex items-center gap-2 bg-fnl-dark border border-purple-900/60 rounded-lg px-3 py-2" data-testid="public-profile-url-row">
          <Globe className="w-4 h-4 text-fnl-pink shrink-0" />
          <span className="text-purple-100 text-xs truncate flex-1" data-testid="public-profile-url">
            {share.url}
          </span>
          <button
            type="button"
            onClick={copy}
            className="text-fnl-pink hover:text-pink-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1 shrink-0"
            data-testid="public-profile-copy-btn"
          >
            {copied ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicProfileShare;
